import type { ElementType, ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { CatalogCardBody, CatalogCardFooter } from '@/shared/components/CatalogCardLayout'
import { Button } from '@/shared/components/ui/button'
import { PictureImg } from '@/shared/components/ui/picture-img'
import { resolveAssetUrl } from '@/shared/lib/resolveAssetUrl'

export function HorizonMediaCard({
  to,
  search,
  title,
  subtitle,
  badges,
  details,
  price,
  actionLabel,
  actionAriaLabel,
  imageUrl,
  imageWebpUrl,
  imageObjectFit = 'cover',
  fallbackIcon: FallbackIcon,
  fallbackTone,
  priority = false,
  footer,
}: {
  to: string
  search?: Record<string, unknown>
  title: string
  subtitle?: ReactNode
  badges?: ReactNode
  details?: ReactNode
  price?: ReactNode
  actionLabel: string
  actionAriaLabel: string
  imageUrl?: string | null
  imageWebpUrl?: string | null
  imageObjectFit?: 'cover' | 'contain'
  fallbackIcon: ElementType
  fallbackTone?: string
  priority?: boolean
  footer?: ReactNode
}) {
  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-[var(--brand-radius-card)] bg-[var(--brand-background)]">
      <div className="relative aspect-[4/3] overflow-hidden rounded-[var(--brand-radius-card)] bg-[var(--brand-surface-strong)]">
        <Link
          to={to}
          search={search}
          tabIndex={-1}
          aria-hidden
          className="block size-full focus:outline-none"
        >
          {imageUrl ? (
            <PictureImg
              webpSrc={imageWebpUrl ? resolveAssetUrl(imageWebpUrl) : undefined}
              src={resolveAssetUrl(imageUrl)}
              alt=""
              aria-hidden="true"
              loading={priority ? 'eager' : 'lazy'}
              decoding="async"
              className={
                imageObjectFit === 'contain'
                  ? 'size-full object-contain p-6 transition duration-200 group-hover:scale-[1.02]'
                  : 'size-full object-cover transition duration-200 group-hover:scale-[1.02]'
              }
              sizes="(min-width: 1280px) 360px, (min-width: 640px) 50vw, 100vw"
              width={640}
              height={480}
            />
          ) : (
            <div
              className="flex size-full items-center justify-center text-[var(--brand-primary)]"
              style={fallbackTone ? { backgroundColor: fallbackTone } : undefined}
            >
              <FallbackIcon
                className={`size-10 ${fallbackTone ? 'text-white' : ''}`}
                aria-hidden
              />
            </div>
          )}
        </Link>
        {badges ? <div className="absolute left-3 top-3 flex flex-wrap gap-2">{badges}</div> : null}
      </div>

      <CatalogCardBody>
        <h3 className="m-0 text-base font-semibold leading-snug text-[var(--brand-text-primary)]">
          <Link
            to={to}
            search={search}
            className="line-clamp-2 text-inherit no-underline hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-ring)]"
          >
            {title}
          </Link>
        </h3>
        {subtitle ? <div className="text-sm text-[var(--brand-text-secondary)]">{subtitle}</div> : null}
        {details ? <div className="text-sm text-[var(--brand-text-secondary)]">{details}</div> : null}
        <CatalogCardFooter className="border-[var(--brand-border-soft)]">
          {price ? <div className="min-w-0">{price}</div> : <span />}
          <Button size="sm" variant="outline" asChild>
            <Link to={to} search={search} aria-label={actionAriaLabel}>
              {actionLabel}
            </Link>
          </Button>
        </CatalogCardFooter>
        {footer ? (
          <CatalogCardFooter className="border-[var(--brand-border-soft)]">{footer}</CatalogCardFooter>
        ) : null}
      </CatalogCardBody>
    </article>
  )
}
